import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

export const SocialInterestPicker = ({ catalog, selected, customKeywords, onSelectedChange, onCustomChange }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const groups = useMemo(() => catalog.reduce((acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  }, {}), [catalog]);

  const toggle = (slug, checked) => {
    onSelectedChange(checked ? [...new Set([...selected, slug])] : selected.filter((item) => item !== slug));
  };
  const add = () => {
    const label = value.replace(/\s+/g, ' ').trim().slice(0, 48);
    const slug = slugify(label);
    if (label.length < 2 || !slug) return;
    if (!customKeywords.some((item) => item.slug === slug)) onCustomChange([...customKeywords, { slug, label }]);
    toggle(slug, true);
    setValue('');
  };
  const remove = (slug) => {
    onCustomChange(customKeywords.filter((item) => item.slug !== slug));
    toggle(slug, false);
  };

  return (
    <div className="space-y-3" data-testid="social-listening-interest-picker">
      <div className="grid gap-4 xl:grid-cols-2">
        {Object.entries(groups).filter(([category]) => category !== 'Custom').map(([category, items]) => (
          <fieldset key={category} className="border-l-2 border-border pl-3" data-testid={`social-interest-group-${slugify(category)}`}>
            <legend className="mb-2 text-[13px] font-semibold uppercase text-muted-foreground">{category}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {items.map((item) => (
                <label key={item.slug} className="flex cursor-pointer items-center gap-2 text-[13px]">
                  <Checkbox
                    checked={selected.includes(item.slug)}
                    onCheckedChange={(checked) => toggle(item.slug, Boolean(checked))}
                    data-testid={`social-interest-${item.slug}-checkbox`}
                  />
                  <span data-testid={`social-interest-${item.slug}-label`}>{item.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
      <div className="border-t border-border pt-3">
        <div className="flex max-w-lg gap-2">
          <Input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); add(); } }}
            placeholder={t('social.addKeywordPlaceholder')}
            maxLength={48}
            className="h-8 text-[13px]"
            data-testid="social-custom-interest-input"
          />
          <Button size="sm" variant="outline" onClick={add} className="h-8 gap-1" data-testid="social-custom-interest-add-button">
            <Plus className="h-3.5 w-3.5" /> {t('social.add')}
          </Button>
        </div>
        {customKeywords.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-testid="social-custom-interest-list">
          {customKeywords.map((item) => (
            <span key={item.slug} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[13px]" data-testid={`social-custom-interest-${item.slug}`}>
              {item.label}
              <button type="button" onClick={() => remove(item.slug)} aria-label={t('social.remove', { label: item.label })} data-testid={`social-custom-interest-${item.slug}-remove`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>}
      </div>
    </div>
  );
};

export default SocialInterestPicker;